import { useCallback, useMemo, type MouseEvent } from 'react'
import { NavLink } from 'react-router-dom'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import cn from 'classnames'
import { Location } from 'history'
import Icon from '../../Common/Icon'
import styles from '../SideMenu.module.scss'
import { IMenuData, ISubMenuData } from '../SideMenuConfig'
import { isItemActive } from '../utils'
import NestedNavItem from './NestedNavItem'

interface IProps extends Partial<IMenuData> {
  label: string
  link: string
  handleItemExpand: (itemName: string) => void
  itemExpanded: boolean
  // Whether the sidebar itself is wide (pinned or hover-expanded). Children are
  // only shown when the sidebar is wide, so a collapsed sidebar doesn't leave
  // blank rows; the item's own expand state is preserved and restored on hover.
  sidebarExpanded: boolean
  location: Location
  toggleSidebar?: () => void
}

// A top-level group whose header navigates to `link` on click (e.g. Projects →
// the projects overview) and expands its children via a separate chevron.
const ExpandableNavItem = ({
  icon,
  label,
  link,
  subItems = [],
  handleItemExpand,
  itemExpanded,
  sidebarExpanded,
  location,
  toggleSidebar,
}: IProps) => {
  const handleExpandClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      handleItemExpand(label)
    },
    [handleItemExpand, label],
  )

  const handleNavigate = useCallback(() => {
    toggleSidebar?.()
  }, [toggleSidebar])

  const isActive = useMemo(
    () => location.pathname === link || isItemActive(subItems, location),
    [location, link, subItems],
  )

  return (
    <>
      <div className={cn({ [styles.active]: isActive })}>
        <div className={styles.navItem}>
          <NavLink to={link} className={styles.expandableNavLink} onClick={handleNavigate}>
            <ListItemIcon className={styles.icon}>{icon}</ListItemIcon>
            <ListItemText
              classes={{ root: styles.labelRoot, primary: styles.label }}
              primary={label}
            />
          </NavLink>
          {subItems.length > 0 && (
            <ListItemButton
              className={styles.chevronButton}
              onClick={handleExpandClick}
              disableRipple
              aria-label={`Toggle ${label}`}
            >
              <Icon
                name="chevron-down"
                size="sm"
                className={cn(styles.chevron, { [styles.chevronOpen]: itemExpanded })}
              />
            </ListItemButton>
          )}
        </div>
      </div>
      <Collapse in={itemExpanded && sidebarExpanded} timeout={250} unmountOnExit>
        <List className={styles.nestedNav} component="div" disablePadding>
          {subItems.map(({ label: itemLabel, link: itemLink, icon: subMenuIcon }: ISubMenuData) => (
            <NestedNavItem
              link={itemLink}
              label={itemLabel}
              key={itemLink}
              icon={subMenuIcon}
              toggleSidebar={toggleSidebar}
            />
          ))}
        </List>
      </Collapse>
    </>
  )
}

export default ExpandableNavItem
